import { useId } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { Input } from "components/common/Input";
import {
    ERROR_TEXT_COLOR,
    SECONDARY_TEXT_COLOR,
} from "components/common/textColors";

import type { TypedNameFieldProps } from "../types";

// Alternative to drawing: the typed name becomes the signature (RGAA 7.3, WCAG 2.5.7)
const TypedNameField = ({
    handleChange,
    hasError,
    inputRef,
    value,
}: TypedNameFieldProps) => {
    const { t } = useTranslation();
    const inputId = useId();
    const errorId = useId();

    const describedBy = hasError ? errorId : undefined;
    const errorMessage = hasError ? (
        <ErrorMessage id={errorId}>
            <ErrorOutlineIcon fontSize="small" />
            <span>{t("signature.fullNameRequired")}</span>
        </ErrorMessage>
    ) : null;

    return (
        <Field>
            <Label htmlFor={inputId}>{t("signature.fullName")}</Label>
            <NameInput
                ref={inputRef}
                id={inputId}
                type="text"
                autoComplete="name"
                value={value}
                onChange={handleChange}
                aria-invalid={hasError}
                aria-describedby={describedBy}
                $hasError={hasError}
            />
            {errorMessage}
        </Field>
    );
};

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    margin-bottom: 1rem;
`;

const Label = styled.label`
    font-size: 15px;
    font-weight: 700;
`;

const NameInput = styled(Input)<{ $hasError: boolean }>`
    height: 44px;
    margin-bottom: 0;
    font-size: 16px;
    border-color: ${(props) =>
        props.$hasError ? ERROR_TEXT_COLOR : SECONDARY_TEXT_COLOR};
    border-width: ${(props) => (props.$hasError ? "2px" : "1px")};
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${ERROR_TEXT_COLOR};
`;

export default TypedNameField;
